// StrategyCard component - secondary (non-featured) strategy selection card
// Layout order: emoji -> name -> description -> button -> tags -> features -> case study
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@design-system/components/button/button';
import { Typography } from '@design-system/components/typography/typography';
import * as styles from './StrategyCard.css';
import { StrategyVM, CaseStudyVM } from '../../view-model/Strategy.viewModel';
import { StrategyCaseStudyCard } from './StrategyCaseStudyCard';

export type StrategyCardProps = {
  strategy: StrategyVM;
  caseStudy: CaseStudyVM | null;
  onSelect: (strategyId: string) => void;
};

const MAX_FEATURES_DISPLAY = 6;

export const StrategyCard = ({
  strategy,
  caseStudy,
  onSelect,
}: StrategyCardProps) => {
  const displayFeatures = strategy.features.slice(0, MAX_FEATURES_DISPLAY);

  const handleSelect = () => {
    onSelect(strategy.id);
  };

  const handleCaseStudyClick = () => {
    if (caseStudy?.documentUrl) {
      window.open(caseStudy.documentUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={styles.card} data-testid={`strategy-card-${strategy.id}`}>
      {strategy.superTitle && (
        <span className={styles.superTitle}>{strategy.superTitle}</span>
      )}

      <div className={styles.header}>
        <Typography
          as="h3"
          variant="title"
          size="large"
          className={styles.name}
        >
          {strategy.name}
        </Typography>
        {strategy.tagline && (
          <Typography variant="body" size="medium" className={styles.tagline}>
            {strategy.tagline}
          </Typography>
        )}
      </div>

      <Button
        variant="filled"
        color="neutral"
        size="large"
        className={styles.button}
        onClick={handleSelect}
        testId={`strategy-select-${strategy.id}`}
      >
        Choose this strategy
      </Button>

      {strategy.pills.length > 0 && (
        <div className={styles.pillsContainer}>
          {strategy.pills.map((pill, index) => (
            <span key={index} className={styles.pill}>
              {pill}
            </span>
          ))}
        </div>
      )}

      {displayFeatures.length > 0 && (
        <ul className={styles.featureList}>
          {displayFeatures.map((feature, index) => (
            <li key={index} className={styles.featureItem}>
              <FontAwesomeIcon icon={faCheck} className={styles.checkIcon} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      )}

      {caseStudy && (
        <div className={styles.caseStudySection}>
          <p className={styles.caseStudyHeading}>{strategy.name} Case Study</p>
          <div className={styles.caseStudyCardWrapper}>
            <StrategyCaseStudyCard
              caseStudy={caseStudy}
              testId={`strategy-case-study-${caseStudy.id}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};
